import { Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Stack, Typography, Divider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getLinkedinInfo,
  saveLinkedinInfo,
  saveLinkedinInfoReset,
  getLinkedinDeals,
  addLinkedinDeal,
  addLinkedinDealReset,
  updateLinkedinDeal,
  updateLinkedinDealReset,
  removeLinkedinDeal,
  removeLinkedinDealReset,
  recoverLinkedinDeal,
  recoverLinkedinDealReset,
} from "../../state/linkedinPromotion/linkedinPromotionSlice";
import { selectLinkedinPromotionState } from "../../state/linkedinPromotion/linkedinPromotionSelector";
import LinkedinDealFormModal from "./LinkedinDealFormModal";
import LinkedinCompanyInfoForm from "./LinkedinCompanyInfoForm";
import LinkedinDealItem from "./LinkedinDealItem";
import { INTEGRATION_TYPE } from "./linkedinDeal";

const LinkedinSlotsModal = ({ onClose, companyInfo }) => {
  const dispatch = useDispatch();
  const {
    info,
    deals,
    isInfoLoading,
    isDealsLoading,
    isInfoSaving,
    isInfoSaveSuccess,
    isAdding,
    isAddSuccess,
    isUpdating,
    isUpdateSuccess,
    isRemoving,
    isRemoveSuccess,
    isRecovering,
    isRecoverSuccess,
  } = useSelector(selectLinkedinPromotionState);

  const [dealToDelete, setDealToDelete] = useState(null);

  // ─── Modal States ──────────────────────────────────────────────
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [selectedDealForModal, setSelectedDealForModal] = useState(null);

  // Fetch on mount
  useEffect(() => {
    if (companyInfo?.id) {
      dispatch(getLinkedinInfo(companyInfo.id));
      dispatch(getLinkedinDeals(companyInfo.id));
    }
  }, [companyInfo?.id, dispatch]);

  // Info save success
  useEffect(() => {
    if (isInfoSaveSuccess) {
      dispatch(saveLinkedinInfoReset());
      // dispatch(getLinkedinInfo(companyInfo.id));
    }
  }, [isInfoSaveSuccess, dispatch]);

  // Deal add success → re-fetch deals and close modal
  useEffect(() => {
    if (isAddSuccess) {
      dispatch(addLinkedinDealReset());
      setIsDealModalOpen(false);
      setSelectedDealForModal(null);
      dispatch(getLinkedinDeals(companyInfo.id));
    }
  }, [isAddSuccess, dispatch, companyInfo?.id]);

  // Deal update success → re-fetch deals and close modal
  useEffect(() => {
    if (isUpdateSuccess) {
      dispatch(updateLinkedinDealReset());
      setIsDealModalOpen(false);
      setSelectedDealForModal(null);
      dispatch(getLinkedinDeals(companyInfo.id));
    }
  }, [isUpdateSuccess, dispatch, companyInfo?.id]);

  // Deal remove success → re-fetch deals
  useEffect(() => {
    if (isRemoveSuccess) {
      dispatch(removeLinkedinDealReset());
      setDealToDelete(null);
      dispatch(getLinkedinDeals(companyInfo.id));
    }
  }, [isRemoveSuccess, dispatch, companyInfo?.id]);

  // Recover success → re-fetch deals
  useEffect(() => {
    if (isRecoverSuccess) {
      dispatch(recoverLinkedinDealReset());
      dispatch(getLinkedinDeals(companyInfo.id));
    }
  }, [isRecoverSuccess, dispatch, companyInfo?.id]);

  // ─── Handlers ──────────────────────────────────────────────────

  const onSubmitInfo = (data) => {
    dispatch(
      saveLinkedinInfo({
        companyId: companyInfo.id,
        linkedInCompanyId: data.linkedInCompanyId,
        linkedInCompanyName: data.linkedInCompanyName,
        type: info?.type || data.type || INTEGRATION_TYPE.MANAGED_BY_ATS,
      })
    );
  };

  const handleAddDeal = () => {
    setSelectedDealForModal(null);
    setIsDealModalOpen(true);
  };

  const handleEditDeal = (deal) => {
    setSelectedDealForModal(deal);
    setIsDealModalOpen(true);
  };

  const handleSaveDeal = (payload) => {
    if (selectedDealForModal?.id) {
      dispatch(
        updateLinkedinDeal({
          ...payload,
          companyId: companyInfo.id,
        })
      );
    } else {
      dispatch(
        addLinkedinDeal({
          ...payload,
          companyId: companyInfo.id,
        })
      );
    }
  };

  const handleRemoveClick = (id) => {
    setDealToDelete(id);
  };

  const handleConfirmClose = () => setDealToDelete(null);

  const handleConfirmDelete = () => {
    if (dealToDelete) {
      dispatch(removeLinkedinDeal(dealToDelete));
    }
  };

  const handleRecoverClick = (id) => {
    dispatch(recoverLinkedinDeal({ id }));
  };

  // ─── Derived state ─────────────────────────────────────────────

  const isCompanyManaged = info?.type === INTEGRATION_TYPE.MANAGED_BY_COMPANY;
  const canAddDeal = isCompanyManaged ? !deals || deals.length === 0 : true;
  const anyBusy = isAdding || isUpdating || isRemoving || isRecovering;

  if (isInfoLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* ─── Section 1: Company LinkedIn Info ─────────────────────── */}
      <LinkedinCompanyInfoForm info={info} onSubmit={onSubmitInfo} isLoading={isInfoSaving} />

      <Divider sx={{ my: 2 }} />

      {/* ─── Section 2: Deals List ────────────────────────────────── */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Promotion Deals
          </Typography>
          {info && (
            <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={handleAddDeal} disabled={anyBusy || !canAddDeal}>
              Add New Deal
            </Button>
          )}
        </Stack>

        {!info && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please save company LinkedIn info first before adding deals.
          </Typography>
        )}

        {isDealsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        <Stack spacing={2}>
          {/* Existing Deals */}
          {Array.isArray(deals) &&
            deals.map((deal) => (
              <LinkedinDealItem key={deal.id} deal={deal} isCompanyManaged={isCompanyManaged} anyBusy={anyBusy} onEdit={handleEditDeal} onDelete={handleRemoveClick} onRecover={handleRecoverClick} />
            ))}
        </Stack>
      </Box>

      {/* ─── Deal Form Modal (Add / Edit) ────────────────────────── */}
      <LinkedinDealFormModal
        open={isDealModalOpen}
        onClose={() => {
          setIsDealModalOpen(false);
          setSelectedDealForModal(null);
        }}
        deal={selectedDealForModal}
        isCompanyManaged={isCompanyManaged}
        onSave={handleSaveDeal}
        isLoading={isAdding || isUpdating}
      />

      {/* Confirmation Dialog */}
      <Dialog open={!!dealToDelete} onClose={handleConfirmClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove LinkedIn Deal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently <strong>delete</strong> this LinkedIn deal for <strong>{companyInfo?.name}</strong> and remove all active job promotions linked to it. This action{" "}
            <strong>cannot be undone</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleConfirmClose} variant="outlined" fullWidth disabled={isRemoving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" fullWidth disabled={isRemoving}>
            {isRemoving ? "Deleting..." : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkedinSlotsModal;
